import { removeCardBody } from '../../../utils/game/physicsWorld';
import { snapCardOrientation } from '../../../utils/game/animations';

// ── Board mesh reconciler ─────────────────────────────────────────────
// The scene graph is a projection of authoritative card state, not an
// accumulation of imperative deltas. One chokepoint enforces it:
//   • EXISTENCE. Every type:'card' mesh in the scene is tracked in
//     host.meshes or host._exitingMeshes; anything else is leaked
//     detritus (the "ghost") and gets disposed.
//   • EXIT SAFETY. A departing mesh is tracked until its fly-to-pile
//     animation completes; a stranded exit (combat gate never clears)
//     is force-disposed after EXIT_DEADLINE_MS so it can never linger.
//   • PROJECTION (snapshot only). On an authoritative snapshot, each
//     surviving mesh adopts the server's tapped/control/faceDown and
//     re-renders, so a visual prop can't disagree with state either.
// Physics is untouched: a surviving mesh is never recreated, so its
// Rapier body and transform persist.

// A stranded fly-to-pile departure (combat gate never clears) must not
// linger forever. After this long an exiting mesh is force-disposed on
// the next reconcile. Generous so the normal staggered casualty
// procession (≈450ms per card) is never cut short.
export const EXIT_DEADLINE_MS = 8000;

export function isCardMesh(obj) {
  // Cosmetic reveal meshes (e.g. the Howl from Beyond deal) are built with
  // createCardMesh (type:'card' + an id) but are deliberately untracked
  // visual-only overlays, NOT board state. The reaper must ignore them or
  // it disposes the first dealt card mid-reveal on the opponent (the only
  // one on screen when the cast's authoritative snapshot lands).
  return !!(obj && obj.userData && obj.userData.type === 'card'
    && obj.userData.cardInstance?.id && !obj.userData.cosmetic);
}

const cardIdOf = (mesh) => mesh?.userData?.cardInstance?.id || null;

// The single place a card mesh is torn down: scene, physics body, GPU
// resources, and every per-card side table. Every removal path funnels
// here so disposal can never be half-done (the half-done state. Out of
// host.meshes but still in the scene. IS the ghost bug).
export function disposeCardMesh(host, mesh) {
  if (!mesh) return;
  const id = cardIdOf(mesh);
  host.evictCardFromGroup?.(id);
  if (host._removeCardBody) host._removeCardBody(mesh);
  else if (host.physicsWorld) removeCardBody(host.physicsWorld, mesh);
  host.scene.scene.remove(mesh);
  mesh.geometry?.dispose();
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  mats.forEach((m) => m?.dispose?.());
  if (id) {
    host.meshes?.delete(id);
    host.lifeHUDs?.delete(id);
    host.statusHUDs?.delete(id);
    host._removeAllCardEffects?.(id);
  }
}

// Pure: which scene card-mesh ids are untracked (neither live nor exiting).
export function diffSceneCardMeshes(sceneCardIds, liveIds, exitingIds) {
  return sceneCardIds.filter((id) => !liveIds.has(id) && !exitingIds.has(id));
}

export function trackExitingMesh(host, id, mesh, now = Date.now()) {
  if (!host._exitingMeshes) host._exitingMeshes = new Map();
  host._exitingMeshes.set(id, { mesh, queuedAt: now });
}

export function untrackExitingMesh(host, id) {
  host._exitingMeshes?.delete(id);
}

// Enforce the existence invariant + the exit deadline. `now` is injectable
// for tests; callers pass Date.now().
export function reconcileBoardMeshes(host, now = Date.now()) {
  const exiting = host._exitingMeshes || (host._exitingMeshes = new Map());

  // 1. Existence: dispose untracked card meshes (ghosts / leaked detritus).
  const children = host.scene?.scene?.children || [];
  const sceneCardMeshes = children.filter(isCardMesh);
  const liveIds = new Set(host.meshes.keys());
  const exitingIds = new Set(exiting.keys());
  const orphanIds = new Set(
    diffSceneCardMeshes(sceneCardMeshes.map((m) => m.userData.cardInstance.id), liveIds, exitingIds),
  );
  for (const mesh of sceneCardMeshes) {
    if (orphanIds.has(mesh.userData.cardInstance.id)) disposeCardMesh(host, mesh);
  }

  // 2. Exit deadline: force-dispose stranded departures.
  for (const [id, rec] of exiting) {
    if (now - (rec.queuedAt || 0) > EXIT_DEADLINE_MS) {
      disposeCardMesh(host, rec.mesh);
      exiting.delete(id);
    }
  }
}

// Authoritative snapshot reconcile: the server's board is ground truth.
//   • exits for cards the server says are gone are force-disposed (the
//     casualty procession is moot after a resync);
//   • surviving meshes adopt the authoritative tapped/control/faceDown
//     and re-project their orientation so a stuck/reverted visual heals;
//   • then the standard existence reconcile reaps any leftover orphan.
export function reconcileOnSnapshot(host, snapshotBoardIds, now = Date.now(), snapshotCards = null) {
  const present = new Set(snapshotBoardIds || []);
  const exiting = host._exitingMeshes || (host._exitingMeshes = new Map());
  for (const [id, rec] of exiting) {
    if (!present.has(id)) { disposeCardMesh(host, rec.mesh); exiting.delete(id); }
  }
  if (Array.isArray(snapshotCards)) {
    for (const snapCard of snapshotCards) {
      const mesh = host.meshes.get(snapCard.id);
      const live = mesh?.userData?.cardInstance;
      if (!live) continue;
      live.tapped = !!snapCard.tapped;
      live.rotated = !!snapCard.rotated;
      live.faceDown = !!snapCard.faceDown;
      if (snapCard._counters !== undefined) live._counters = snapCard._counters;
      snapCardOrientation(mesh, live);
      // Re-affirm the level-counter token after a state sync (resume /
      // reconnect / late-join). The count + orientation may have moved
      // since this client last built the mesh.
      host._refreshCounterHUD?.(snapCard.id);
    }
  }
  reconcileBoardMeshes(host, now);
}
